import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, Play, FileDown, Trash2, Eye, EyeOff, ChevronRight } from "lucide-react";
import { AuditDetail, RatingBadge } from "@/components/audit/AuditDetail";

export default function Audits() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [audits, setAudits] = useState([]);
  const [setting, setSetting] = useState(null);
  const [active, setActive] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/audits", { params: isAdmin && customerId ? { customer_id: customerId } : {} });
      setAudits(data);
      if (isAdmin && customerId) { const s = await api.get(`/audits/settings/${customerId}`); setSetting(s.data); } else setSetting(null);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [isAdmin, customerId]);
  useEffect(() => { if (isAdmin) api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {}); }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!customerId) return; setRunning(true);
    try { const { data } = await api.post(`/audits/${customerId}/run`); toast.success(`Audit complete — ${data.overall.toUpperCase()}`); setActive(data); load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setRunning(false); }
  };
  const open = async (id) => { try { const { data } = await api.get(`/audits/${id}`); setActive(data); } catch (err) { toast.error(formatApiError(err)); } };
  const toggleDefault = async (v) => {
    try { const { data } = await api.put(`/audits/settings/${customerId}`, { visible_to_customer: v }); setSetting(data); toast.success(v ? "Customer can now view and download their audits" : "Audits hidden from customer"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const toggleOne = async (a, v) => {
    try { await api.patch(`/audits/${a.id}/visibility`, { visible_to_customer: v }); load(); if (active?.id === a.id) setActive({ ...active, visible_to_customer: v }); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (a) => {
    if (!window.confirm("Delete this audit run?")) return;
    try { await api.delete(`/audits/${a.id}`); if (active?.id === a.id) setActive(null); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const pdfUrl = (a) => `${process.env.REACT_APP_BACKEND_URL}/api/audits/${a.id}/report.pdf`;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="audits-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="tag-mono text-blue-700 mb-1">{isAdmin ? "ADMIN" : "COMPLIANCE"}</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-slate-900">Compliance Audit</h1>
          <p className="text-slate-600 mt-1">{isAdmin ? "Run an FMCSA-style mock compliance review against everything on file for a customer — safety fitness factors 1–5 with a proposed rating and corrective-action list." : "Mock compliance reviews shared with you by your consultant."}</p>
        </div>
        {isAdmin && (
          <Card className="p-3 flex items-center gap-3 flex-wrap" data-testid="audit-run-card">
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="w-full sm:w-72" data-testid="audit-customer-select"><SelectValue placeholder="Choose a customer…" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={run} disabled={!customerId || running} className="bg-blue-700 hover:bg-blue-800" data-testid="audit-run-button"><Play className="h-4 w-4 mr-1" />{running ? "Auditing…" : "Run audit"}</Button>
            {setting && <label className="flex items-center gap-2 text-xs text-slate-700 border-l pl-3 ml-1"><Switch checked={!!setting.visible_to_customer} onCheckedChange={toggleDefault} data-testid="audit-visibility-default-toggle" /><span>Customer can view &amp; download</span></label>}
          </Card>
        )}
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-4">
        <Card className="overflow-hidden" data-testid="audit-history">
          <div className="tag-mono text-slate-500 px-4 py-3 border-b border-slate-200">AUDIT HISTORY{isAdmin && customerId ? "" : " · ALL"}</div>
          {audits.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">{isAdmin ? "No audits yet. Pick a customer and run one." : "No audits have been shared with you yet."}</div>}
          {audits.map((a) => (
            <button type="button" key={a.id} onClick={() => open(a.id)} className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${active?.id === a.id ? "bg-blue-50" : ""}`} data-testid={`audit-row-${a.id}`}>
              <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-slate-900 truncate">{a.company}</span><RatingBadge rating={a.overall} /></div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap"><span className="font-mono">{a.run_at.slice(0, 10)}</span>· {a.run_by} · {a.counts.pass}✓ {a.counts.fail}✗ {a.counts.missing}?{isAdmin && (a.visible_to_customer ? <Eye className="h-3.5 w-3.5 text-emerald-600" title="Visible to customer" /> : <EyeOff className="h-3.5 w-3.5 text-slate-400" title="Hidden from customer" />)}<ChevronRight className="h-3.5 w-3.5 ml-auto" /></div>
            </button>
          ))}
        </Card>
        <Card className="p-5" data-testid="audit-detail-card">
          {!active ? <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-slate-500 text-sm gap-2"><ClipboardCheck className="h-8 w-8 text-slate-300" />Select an audit to see the factor ratings and corrective actions.</div> : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div><div className="text-lg font-bold text-slate-900">{active.company}</div><div className="text-xs text-slate-500 font-mono">{active.run_at.replace("T", " ").slice(0, 16)} · {active.run_by} · {active.scope.drivers} drivers · {active.scope.vehicles} units</div></div>
                <div className="flex items-center gap-2 flex-wrap">
                  {isAdmin && <label className="flex items-center gap-2 text-xs text-slate-700"><Switch checked={!!active.visible_to_customer} onCheckedChange={(v) => toggleOne(active, v)} data-testid="audit-visibility-toggle" /><Label className="text-xs">Share with customer</Label></label>}
                  <a href={pdfUrl(active)} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" data-testid="audit-pdf-button"><FileDown className="h-4 w-4 mr-1" />PDF report</Button></a>
                  {isAdmin && <Button variant="ghost" size="sm" className="text-rose-700" onClick={() => remove(active)} data-testid="audit-delete-button"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
              <AuditDetail audit={active} />
            </>
          )}
        </Card>
      </div>
      {!isAdmin && audits.length > 0 && <p className="text-xs text-slate-500 mt-3"><Badge variant="outline" className="mr-1">Note</Badge>These are consultant mock reviews modelled on FMCSA safety fitness factors — not official FMCSA determinations.</p>}
    </div>
  );
}
